/**
 * CheckInService - CRUD operations for check-ins
 *
 * Provides type-safe methods for creating, reading, updating, and deleting check-ins
 * with real-time sync, offline support, and validation.
 *
 * @see WI-022 - Check-In Service
 * @see WI-049 - Late Check-In Recovery (escalating status detection + dialog)
 * @see checkin.models.ts - CheckIn interfaces
 * @see data-schemas.md Section: CheckIn
 */

import { Injectable, Injector, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Timestamp } from '@angular/fire/firestore';
import { ModalController } from '@ionic/angular/standalone';
import { FirestoreService } from '@core/services/firestore.service';
import { AuthService } from '@core/services/auth.service';
import { UserService } from '@core/services/user.service';
import { AnalyticsService } from '@core/services/analytics.service';
import { QueryConstraint } from '@core/services/database.interface';
import {
  CheckIn,
  CreateCheckInRequest,
  UpdateCheckInRequest,
  CheckInFilters,
  generateDedupeKey,
} from '@shared/models/checkin.models';
import { CheckInValidators } from '@shared/models/checkin.validators';
import { AnalyticsEvent } from '@core/types/analytics.types';
import { devLog, devWarn } from '@core';
import {
  LateCheckinDialogComponent,
  LateCheckinDialogResult,
} from '../components/late-checkin-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class CheckInService {
  private firestore = inject(FirestoreService);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private analytics = inject(AnalyticsService);
  // WI-049: Injector used to resolve ModalController lazily (only when the
  // late check-in dialog is presented). ModalController is provided by
  // provideIonicAngular() at bootstrap, not providedIn 'root', so eager
  // injection would fail in non-Ionic test environments.
  private injector = inject(Injector);

  /**
   * Create a new check-in
   *
   * @param request - CreateCheckInRequest payload
   * @returns Promise resolving to created CheckIn
   * @throws Error if validation fails, user not authenticated, or duplicate check-in exists
   */
  async createCheckIn(request: CreateCheckInRequest): Promise<CheckIn> {
    // Validate request
    const errors = CheckInValidators.validateCreateRequest(request);
    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`);
    }

    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      throw new Error('Not authenticated');
    }

    const userId = currentUser.uid;
    const user = await this.userService.loadUser(userId);
    if (!user) {
      throw new Error('User profile not found');
    }

    // Generate check-in ID
    const checkInId = this.generateId();

    // Convert trigger time to Firestore timestamp
    const triggerTime = Timestamp.fromDate(request.triggerTime);

    // Use user's default settings or request overrides
    const graceWindow = request.graceWindow ?? user.settings.defaultGraceWindow;
    const nudgeDelay = request.nudgeDelay ?? user.settings.defaultNudgeDelay;

    // Generate dedupe key
    const dedupeKey = generateDedupeKey(userId, request.triggerTime);

    // Check for duplicate (same user, same trigger time within 1 minute window)
    const existingCheckIns = await this.getCheckIns({
      startDate: new Date(request.triggerTime.getTime() - 60000), // 1 min before
      endDate: new Date(request.triggerTime.getTime() + 60000), // 1 min after
    });

    if (existingCheckIns.some((c) => c.dedupeKey === dedupeKey)) {
      throw new Error('Check-in already exists for this time');
    }

    // Use server timestamp for document-level fields, Timestamp.now() for arrays
    // Firebase does not support serverTimestamp() inside arrays
    const checkIn: CheckIn = {
      id: checkInId,
      userId: userId,
      type: 'time',
      triggerTime: triggerTime,
      graceWindow: graceWindow,
      nudgeDelay: nudgeDelay,
      status: 'scheduled',
      statusHistory: [
        {
          status: 'scheduled',
          timestamp: Timestamp.now(),
          reason: 'Check-in created',
        },
      ],
      contactIds: request.contactIds || [],
      ...(request.note && { note: request.note }),
      lastUserActionAt: this.firestore.fieldValue.serverTimestamp() as unknown as Timestamp,
      createdAt: this.firestore.fieldValue.serverTimestamp() as unknown as Timestamp,
      dedupeKey: dedupeKey,
    };

    // Write to Firestore
    const path = `users/${userId}/checkIns/${checkInId}`;
    await this.firestore.setDoc(path, checkIn);

    devLog('[CheckInService] Check-in created:', { checkInId, userId, triggerTime: request.triggerTime.toISOString() });

    // Track analytics event
    const now = new Date();
    const triggerTimeOffset = Math.round((request.triggerTime.getTime() - now.getTime()) / (1000 * 60));
    this.analytics.logEvent(AnalyticsEvent.UI_CHECKIN_CREATE_SUCCESS, {
      checkin_id: checkInId,
      trigger_time_offset: triggerTimeOffset,
      contact_count: request.contactIds?.length || 0,
      has_note: !!request.note,
      timestamp: now.toISOString(),
    });

    return checkIn;
  }

  /**
   * Get a single check-in by ID
   *
   * @param checkInId - Check-in document ID
   * @returns Promise resolving to CheckIn or null if not found
   * @throws Error if user not authenticated
   */
  async getCheckIn(checkInId: string): Promise<CheckIn | null> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      throw new Error('Not authenticated');
    }

    const path = `users/${currentUser.uid}/checkIns/${checkInId}`;
    return await this.firestore.getDoc<CheckIn>(path);
  }

  /**
   * Get user's check-ins with optional filtering
   *
   * @param filters - Optional CheckInFilters (status, date range, limit)
   * @returns Promise resolving to array of CheckIn documents
   * @throws Error if user not authenticated
   */
  async getCheckIns(filters?: CheckInFilters): Promise<CheckIn[]> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      throw new Error('Not authenticated');
    }

    const path = `users/${currentUser.uid}/checkIns`;
    const constraints = this.buildQueryConstraints(filters);

    return await this.firestore.getCollection<CheckIn>(path, constraints);
  }

  /**
   * Update an existing check-in
   *
   * @param checkInId - Check-in document ID
   * @param updates - UpdateCheckInRequest with fields to update
   * @throws Error if user not authenticated, check-in not found, or invalid status transition
   */
  async updateCheckIn(
    checkInId: string,
    updates: UpdateCheckInRequest
  ): Promise<void> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      throw new Error('Not authenticated');
    }

    const path = `users/${currentUser.uid}/checkIns/${checkInId}`;

    // Get current check-in
    const checkIn = await this.firestore.getDoc<CheckIn>(path);
    if (!checkIn) {
      throw new Error('Check-in not found');
    }

    // Validate status transition
    if (
      updates.status &&
      !CheckInValidators.validateStatusTransition(checkIn.status, updates.status)
    ) {
      throw new Error(
        `Invalid status transition: ${checkIn.status} -> ${updates.status}`
      );
    }

    // Build update object
    const updateData: Partial<CheckIn> = {};

    if (updates.status) {
      updateData.status = updates.status;

      // Append to status history (use Timestamp.now() - serverTimestamp not supported in arrays)
      const newHistoryEntry = {
        status: updates.status,
        timestamp: Timestamp.now(),
        reason: updates.note || 'Status updated',
      };
      updateData.statusHistory = [...checkIn.statusHistory, newHistoryEntry];

      // Set resolved/completed timestamps (use server timestamp)
      if (updates.status === 'resolved') {
        updateData.resolvedAt = this.firestore.fieldValue.serverTimestamp() as unknown as Timestamp;
      }
    }

    if (updates.note !== undefined) {
      updateData.note = updates.note;
    }

    if (updates.lastUserActionAt) {
      // Convert Date to Firestore Timestamp
      updateData.lastUserActionAt = Timestamp.fromDate(
        updates.lastUserActionAt
      );
    }

    await this.firestore.updateDoc(path, updateData);

    devLog('[CheckInService] Check-in updated:', { checkInId, updates });

    // Track analytics events based on status transition
    if (updates.status === 'resolved') {
      const now = new Date();
      const triggerTime = new Date(checkIn.triggerTime.seconds * 1000);
      const resolutionDelay = Math.round((now.getTime() - triggerTime.getTime()) / (1000 * 60));

      this.analytics.logEvent(AnalyticsEvent.UI_CHECKIN_RESOLVED, {
        checkin_id: checkInId,
        resolution_type: resolutionDelay > 0 ? 'late' : 'manual',
        resolution_delay: resolutionDelay,
        timestamp: now.toISOString(),
      });
    } else if (updates.status === 'cancelled') {
      this.analytics.logEvent(AnalyticsEvent.UI_CHECKIN_CANCELLED, {
        checkin_id: checkInId,
        previous_status: checkIn.status,
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Delete a check-in
   *
   * @param checkInId - Check-in document ID
   * @throws Error if user not authenticated
   */
  async deleteCheckIn(checkInId: string): Promise<void> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      throw new Error('Not authenticated');
    }

    const path = `users/${currentUser.uid}/checkIns/${checkInId}`;
    await this.firestore.deleteDoc(path);

    devLog('[CheckInService] Check-in deleted:', { checkInId });

    // Note: Delete operation tracked as CHECKIN_CANCELLED instead of separate delete event
    // to maintain consistent analytics schema
  }

  /**
   * Watch a single check-in for real-time updates
   *
   * @param checkInId - Check-in document ID
   * @returns Observable that emits CheckIn on changes
   * @throws Error if user not authenticated
   */
  watchCheckIn(checkInId: string): Observable<CheckIn | null> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      throw new Error('Not authenticated');
    }

    const path = `users/${currentUser.uid}/checkIns/${checkInId}`;
    return this.firestore.docListener<CheckIn>(path);
  }

  /**
   * Watch user's check-ins collection for real-time updates
   *
   * @param filters - Optional CheckInFilters (status, date range, limit)
   * @returns Observable that emits array of CheckIn documents on changes
   * @throws Error if user not authenticated
   */
  watchCheckIns(filters?: CheckInFilters): Observable<CheckIn[]> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      throw new Error('Not authenticated');
    }

    const path = `users/${currentUser.uid}/checkIns`;
    const constraints = this.buildQueryConstraints(filters);
    return this.firestore.collectionListener<CheckIn>(path, constraints);
  }

  /**
   * Check whether a check-in is a "late" check-in requiring the recovery
   * flow - i.e., escalation has started and contacts have been alerted.
   *
   * @param checkIn - Check-in to inspect
   * @returns True if the check-in is currently escalating
   */
  isLateCheckIn(checkIn: CheckIn): boolean {
    return checkIn.status === 'escalating';
  }

  /**
   * Resolve a check-in (mark as successfully completed)
   *
   * WI-049: If the check-in is currently 'escalating' (contacts already
   * alerted), a warning dialog is shown first so the user can confirm and
   * optionally add a note for their contacts. Cancelling the dialog keeps
   * the check-in unresolved. The escalating -> resolved transition triggers
   * the server-side sendResolvedNotification function which sends
   * "User is safe" notifications to previously alerted contacts.
   *
   * @param checkInId - Check-in document ID
   * @param note - Optional note explaining late check-in or context
   * @throws Error if user not authenticated or check-in not found
   */
  async resolveCheckIn(checkInId: string, note?: string): Promise<void> {
    const checkIn = await this.getCheckIn(checkInId);
    if (!checkIn) {
      throw new Error('Check-in not found');
    }

    let resolutionNote = note;

    // WI-049: Late check-in detection - show recovery dialog for escalating status
    if (this.isLateCheckIn(checkIn)) {
      devLog('[CheckInService] Late check-in detected (escalating)', { checkInId });

      const result = await this.presentLateCheckInDialog(checkIn, note);

      if (!result.confirmed) {
        // User cancelled - check-in stays unresolved, no notifications sent
        devLog('[CheckInService] Late check-in cancelled by user', { checkInId });
        this.analytics.logEvent(AnalyticsEvent.UI_CHECKIN_RESOLVED, {
          checkin_id: checkInId,
          resolution_type: 'late_cancelled',
          timestamp: new Date().toISOString(),
        });
        return;
      }

      resolutionNote = result.note ?? note;
    }

    await this.updateCheckIn(checkInId, {
      status: 'resolved',
      note: resolutionNote,
      lastUserActionAt: new Date(),
    });
  }

  /**
   * Present the late check-in recovery dialog (WI-049)
   *
   * Shown when the user checks in while the check-in is escalating.
   * Warns that contacts were already alerted and collects an optional
   * note (max 200 chars) to include in the "User is safe" notification.
   *
   * @param checkIn - The escalating check-in being resolved
   * @param initialNote - Note already entered upstream (pre-fills the dialog)
   * @returns Dialog result - confirmed flag and optional note
   */
  private async presentLateCheckInDialog(
    checkIn: CheckIn,
    initialNote?: string
  ): Promise<LateCheckinDialogResult> {
    // Look up the user's name for the notification preview (best-effort)
    let userName = '';
    try {
      const currentUser = this.authService.getCurrentUser();
      if (currentUser) {
        const user = await this.userService.loadUser(currentUser.uid);
        userName = user?.name ?? '';
      }
    } catch (error: unknown) {
      devWarn('[CheckInService] Could not load user name for late check-in dialog', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }

    const modalController = this.injector.get(ModalController);
    const modal = await modalController.create({
      component: LateCheckinDialogComponent,
      componentProps: {
        checkInId: checkIn.id,
        userName,
        note: initialNote ?? '',
      },
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss<LateCheckinDialogResult>();

    if (role === 'confirm' && data?.confirmed) {
      return { confirmed: true, note: data.note };
    }

    return { confirmed: false };
  }

  /**
   * Cancel a check-in (user manually cancels before trigger time)
   *
   * @param checkInId - Check-in document ID
   * @throws Error if user not authenticated or check-in not found
   */
  async cancelCheckIn(checkInId: string): Promise<void> {
    await this.updateCheckIn(checkInId, {
      status: 'cancelled',
      lastUserActionAt: new Date(),
    });
  }

  /**
   * Undo a resolved check-in (return to scheduled status)
   *
   * Allows users to undo an accidental check-in within the time limit.
   * The UI enforces a 5-minute window; this service method validates the transition.
   *
   * Note: resolvedAt is not cleared - it remains as historical data showing
   * when the check-in was resolved before being undone.
   *
   * @param checkInId - Check-in document ID
   * @throws Error if user not authenticated, check-in not found, or invalid transition
   */
  async undoCheckIn(checkInId: string): Promise<void> {
    await this.updateCheckIn(checkInId, {
      status: 'scheduled',
      lastUserActionAt: new Date(),
    });
  }

  /**
   * Snooze a check-in (delay trigger time by specified duration)
   *
   * Updates check-in status to 'snoozed' and calculates new trigger time
   * based on current time plus duration. Used when user needs more time
   * before completing check-in.
   *
   * @param checkInId - Check-in document ID
   * @param durationMinutes - Minutes to snooze (5, 15, 30, 60)
   * @throws Error if user not authenticated, check-in not found, or invalid status
   */
  async snoozeCheckIn(checkInId: string, durationMinutes: number): Promise<void> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      throw new Error('Not authenticated');
    }

    const path = `users/${currentUser.uid}/checkIns/${checkInId}`;

    // Get current check-in
    const checkIn = await this.firestore.getDoc<CheckIn>(path);
    if (!checkIn) {
      throw new Error('Check-in not found');
    }

    // Only allow snoozing active or nudge check-ins
    const snoozableStatuses = ['active', 'nudge'];
    if (!snoozableStatuses.includes(checkIn.status)) {
      throw new Error(
        `Cannot snooze check-in with status '${checkIn.status}'. Only 'active' or 'nudge' check-ins can be snoozed.`
      );
    }

    // Calculate new trigger time
    const now = new Date();
    const newTriggerTime = new Date(now.getTime() + durationMinutes * 60 * 1000);

    // Build update object
    const newHistoryEntry = {
      status: 'snoozed' as const,
      timestamp: Timestamp.now(),
      reason: `Snoozed for ${durationMinutes} minutes`,
    };

    const updateData: Partial<CheckIn> = {
      status: 'snoozed',
      triggerTime: Timestamp.fromDate(newTriggerTime),
      statusHistory: [...checkIn.statusHistory, newHistoryEntry],
      lastUserActionAt: this.firestore.fieldValue.serverTimestamp() as unknown as Timestamp,
    };

    await this.firestore.updateDoc(path, updateData);

    devLog('[CheckInService] Check-in snoozed:', {
      checkInId,
      durationMinutes,
      newTriggerTime: newTriggerTime.toISOString(),
    });

    this.analytics.logEvent(AnalyticsEvent.UI_CHECKIN_SNOOZED, {
      checkin_id: checkInId,
      snooze_duration: durationMinutes,
      timestamp: new Date().toISOString(),
    });
  }

  // ==================== PRIVATE HELPERS ====================

  /**
   * Build Firestore query constraints from CheckInFilters
   *
   * @param filters - Optional CheckInFilters
   * @returns Array of QueryConstraint objects for Firestore query
   */
  private buildQueryConstraints(filters?: CheckInFilters): QueryConstraint[] {
    const constraints: QueryConstraint[] = [];

    if (filters?.status) {
      if (Array.isArray(filters.status)) {
        // Multiple statuses: use 'in' operator
        constraints.push({
          type: 'where',
          field: 'status',
          operator: 'in',
          value: filters.status,
        });
      } else {
        // Single status
        constraints.push({
          type: 'where',
          field: 'status',
          operator: '==',
          value: filters.status,
        });
      }
    }

    if (filters?.startDate) {
      // Convert Date to Firestore Timestamp for query filter
      constraints.push({
        type: 'where',
        field: 'triggerTime',
        operator: '>=',
        value: Timestamp.fromDate(filters.startDate),
      });
    }

    if (filters?.endDate) {
      // Convert Date to Firestore Timestamp for query filter
      constraints.push({
        type: 'where',
        field: 'triggerTime',
        operator: '<=',
        value: Timestamp.fromDate(filters.endDate),
      });
    }

    // Default sort: trigger time descending (most recent first)
    constraints.push({
      type: 'orderBy',
      field: 'triggerTime',
      direction: 'desc',
    });

    if (filters?.limit) {
      constraints.push({
        type: 'limit',
        value: filters.limit,
      });
    }

    return constraints;
  }

  /**
   * Generate unique check-in ID
   * Format: checkin_{timestamp}_{random}
   *
   * @returns Unique check-in ID string
   */
  private generateId(): string {
    return `checkin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
